import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import './StudentTrainingManagement.css';
import {
  createCourseEnrolment,
  createInstructorProfile,
  createStudentProfile,
  fetchStudentTrainingData,
  publishSyllabus,
  updateCourseEnrolmentInstructor,
  updateInstructorNotes,
} from './studentTrainingService';
import {
  buildTrainingProgressSummary,
  buildTrainingRecordSummary,
  downloadTrainingRecordCsv,
  latestTrainingItemOutcomes,
} from './trainingRecordUtils';
import {
  getTrainingExerciseReferenceDetails,
  presentTrainingCriterion,
} from './trainingLessonAssessment';

function memberLabel(member) {
  return member.full_name
    ? `${member.full_name}${member.email ? ` · ${member.email}` : ''}`
    : member.email || member.id;
}

function bookingFlightTime(booking) {
  const value = booking?.briefing_data?.techLogSubmission?.flightTime;
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
}

function bookingAssessmentSummary(bookings, enrolmentId) {
  const assessmentsByItem = new Map();
  let assessmentCount = 0;
  const matchingBookings = bookings.filter(
    (booking) =>
      booking.booking_type === 'training' &&
      String(booking.course_enrolment_id || '') === String(enrolmentId)
  );

  matchingBookings.forEach((booking) => {
    const history = Array.isArray(booking.briefing_data?.trainingAssessments)
      ? booking.briefing_data.trainingAssessments
      : [];
    history.forEach((assessment) => {
      if (!assessment || typeof assessment !== 'object') return;
      assessmentCount += 1;
      (Array.isArray(assessment.exerciseAssessments)
        ? assessment.exerciseAssessments
        : []
      ).forEach((item) => {
        if (
          item?.itemId &&
          ['satisfactory', 'needs_practice'].includes(item.outcome)
        ) {
          const previous = assessmentsByItem.get(String(item.itemId));
          const currentTime = Date.parse(assessment.assessedAt || '') || 0;
          const previousTime = previous?.assessedAt || 0;
          if (!previous || currentTime >= previousTime) {
            assessmentsByItem.set(String(item.itemId), {
              outcome: item.outcome,
              assessedAt: currentTime,
            });
          }
        }
      });
    });
  });

  const latestOutcomes = [...assessmentsByItem.values()];
  return {
    bookingCount: matchingBookings.length,
    assessmentCount,
    assessedItemCount: latestOutcomes.length,
    satisfactoryCount: latestOutcomes.filter(
      (item) => item.outcome === 'satisfactory'
    ).length,
    needsPracticeCount: latestOutcomes.filter(
      (item) => item.outcome === 'needs_practice'
    ).length,
  };
}

function assessmentOutcomeLabel(outcome) {
  if (outcome === 'satisfactory') return 'Satisfactory';
  if (outcome === 'needs_practice') return 'Needs practice';
  return 'Not yet assessed';
}

function formatTrainingMinutes(value) {
  const minutes = Number(value) || 0;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function trainingRecordTypeLabel(type) {
  if (type === 'lesson_assessment') return 'Lesson assessment';
  if (type === 'theory_exam') return 'Theory exam';
  if (type === 'skill_test') return 'Skill test';
  if (type === 'credit_review') return 'School-reviewed credit';
  if (type === 'flight') return 'Flight record';
  return type || 'Training record';
}

function isValidDateOnly(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

const STUDENT_RECORD_SECTIONS = [
  'Overview',
  'Syllabus & Progress',
  'Lessons & Assessments',
  'Flight Hours',
  'Training History',
];

export default function StudentTrainingManagement() {
  const navigate = useNavigate();
  const session = useSession();
  const organisationId = session?.profile?.organisation_id || '';
  const isActiveOrganisationAdmin = Boolean(
    session?.profile &&
    session.profile.is_active !== false &&
    ['admin', 'owner'].includes(String(session.profile.role || '').trim().toLowerCase())
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedInstructorMemberId, setSelectedInstructorMemberId] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedSyllabusId, setSelectedSyllabusId] = useState('');
  const [selectedInstructorId, setSelectedInstructorId] = useState('');
  const [publicationDates, setPublicationDates] = useState({});
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [instructorNotesDrafts, setInstructorNotesDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState('');
  const savingRef = useRef(false);
  const [actionError, setActionError] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [activeRecordSection, setActiveRecordSection] = useState('Overview');
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseStatusFilter, setExerciseStatusFilter] = useState('all');
  const [expandedExercises, setExpandedExercises] = useState({});

  async function reloadData() {
    setLoading(true);
    setLoadError('');
    try {
      const result = await fetchStudentTrainingData(supabase, organisationId);
      if (result.error) {
        setLoadError(`Could not load student training data. ${result.error.message || 'Please try again.'}`);
        setData(null);
      } else {
        setData(result.data);
      }
    } catch (error) {
      setLoadError(`Could not load student training data. ${error?.message || 'Please try again.'}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await fetchStudentTrainingData(supabase, organisationId);
        if (cancelled) return;
        if (result.error) {
          setLoadError(`Could not load student training data. ${result.error.message || 'Please try again.'}`);
          setData(null);
        } else {
          setData(result.data);
          setLoadError('');
        }
      } catch (error) {
        if (cancelled) return;
        setLoadError(`Could not load student training data. ${error?.message || 'Please try again.'}`);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    setLoading(true);
    load();
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const membersWithoutProfiles = useMemo(() => {
    if (!data) return [];
    const existingUserIds = new Set(data.students.map((student) => String(student.user_id)));
    const search = memberSearch.trim().toLowerCase();
    return data.members.filter(
      (member) =>
        member.is_active !== false &&
        !existingUserIds.has(String(member.id)) &&
        (!search ||
          String(member.full_name || '').toLowerCase().includes(search) ||
          String(member.email || '').toLowerCase().includes(search))
    );
  }, [data, memberSearch]);

  const membersWithoutInstructorProfiles = useMemo(() => {
    if (!data) return [];
    const existingUserIds = new Set(
      data.instructors.map((instructor) => String(instructor.user_id))
    );
    return data.members.filter(
      (member) =>
        member.is_active !== false &&
        !existingUserIds.has(String(member.id))
    );
  }, [data]);

  const selectedStudent = data?.students.find(
    (student) => String(student.id) === String(selectedStudentId)
  );
  useEffect(() => {
    if (!data) return;
    const selectedExists = data.students.some(
      (student) => String(student.id) === String(selectedStudentId)
    );
    if (!selectedExists) {
      setSelectedStudentId(
        data.students.find((student) => student.status === 'active')?.id || ''
      );
    }
  }, [data, selectedStudentId]);

  const selectedStudentMember = data?.members.find(
    (member) => String(member.id) === String(selectedStudent?.user_id)
  );
  const activeCourses = (data?.courses || []).filter(
    (course) =>
      course.status === 'active' &&
      course.enrolment_mode === 'new_and_historical'
  );
  const activeInstructorProfiles = (data?.instructors || []).filter(
    (instructor) => instructor.status === 'active'
  );
  const draftSyllabusVersions = (data?.syllabusVersions || []).filter(
    (version) => version.status === 'draft'
  );
  const eligibleSyllabi = useMemo(() => {
    if (!data || !selectedCourseId) return [];
    const today = new Date().toISOString().slice(0, 10);
    return data.syllabusVersions.filter(
      (version) =>
        String(version.course_id) === String(selectedCourseId) &&
        version.status === 'published' &&
        (!version.effective_from || version.effective_from <= today) &&
        (!version.effective_to || version.effective_to >= today)
    );
  }, [data, selectedCourseId]);

  useEffect(() => {
    if (!eligibleSyllabi.some((syllabus) => String(syllabus.id) === String(selectedSyllabusId))) {
      setSelectedSyllabusId(eligibleSyllabi[0]?.id || '');
    }
  }, [eligibleSyllabi, selectedSyllabusId]);

  const selectedStudentEnrolments = (data?.enrolments || []).filter(
    (enrolment) =>
      String(enrolment.student_profile_id) === String(selectedStudentId)
  );

  async function handleCreateStudent(event) {
    event.preventDefault();
    if (savingRef.current || !selectedMemberId) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAction('student');
    setActionError('');
    setActionStatus('');
    try {
      const result = await createStudentProfile(
        supabase,
        organisationId,
        selectedMemberId
      );
      if (result.error) throw result.error;
      setSelectedStudentId(result.data.id);
      setSelectedMemberId('');
      setMemberSearch('');
      setActionStatus('Student profile created.');
      await reloadData();
    } catch (error) {
      setActionError(`Could not create student profile. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  async function handleCreateInstructor(event) {
    event.preventDefault();
    if (savingRef.current || !selectedInstructorMemberId) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAction('instructor');
    setActionError('');
    setActionStatus('');
    try {
      const result = await createInstructorProfile(
        supabase,
        organisationId,
        selectedInstructorMemberId
      );
      if (result.error) throw result.error;
      setSelectedInstructorMemberId('');
      setActionStatus('Instructor profile created with active training permissions.');
      await reloadData();
    } catch (error) {
      setActionError(`Could not create instructor profile. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  async function handleSaveInstructorNotes(instructorProfileId) {
    const notesKey = String(instructorProfileId);
    const notes = Object.prototype.hasOwnProperty.call(
      instructorNotesDrafts,
      notesKey
    )
      ? instructorNotesDrafts[notesKey]
      : data?.instructorNotes?.[notesKey] || '';
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAction(`instructor-notes-${notesKey}`);
    setActionError('');
    setActionStatus('');
    try {
      const result = await updateInstructorNotes(supabase, {
        organisationId,
        instructorProfileId,
        notes,
      });
      if (result.error) throw result.error;
      setInstructorNotesDrafts((current) => {
        const next = { ...current };
        delete next[notesKey];
        return next;
      });
      setActionStatus('Private instructor notes saved.');
      await reloadData();
    } catch (error) {
      setActionError(`Could not save instructor notes. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  async function handleCreateEnrolment(event) {
    event.preventDefault();
    if (
      savingRef.current ||
      !selectedStudent ||
      selectedStudent.status !== 'active'
    ) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAction('enrolment');
    setActionError('');
    setActionStatus('');
    try {
      const result = await createCourseEnrolment(supabase, {
        organisationId,
        studentProfileId: selectedStudent.id,
        courseId: selectedCourseId,
        syllabusVersionId: selectedSyllabusId,
        instructorProfileId: selectedInstructorId,
      });
      if (result.error) throw result.error;
      setActionStatus('Student enrolled on the selected published syllabus.');
      await reloadData();
    } catch (error) {
      setActionError(`Could not create course enrolment. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  async function handlePublishSyllabus(version) {
    if (savingRef.current || !isActiveOrganisationAdmin || !data) return;
    const dateDraft = publicationDates[String(version.id)] || {};
    const effectiveFrom = dateDraft.effectiveFrom || '';
    const effectiveTo = dateDraft.effectiveTo || '';
    const course = data.courses.find(
      (candidate) => String(candidate.id) === String(version.course_id)
    );
    const exercises = data.trainingExercises.filter(
      (exercise) => String(exercise.syllabus_version_id) === String(version.id)
    );
    const exerciseIds = new Set(exercises.map((exercise) => String(exercise.id)));
    const items = data.trainingExerciseItems.filter(
      (item) => exerciseIds.has(String(item.exercise_id))
    );

    if (
      version.status !== 'draft' ||
      !course ||
      course.status !== 'active' ||
      course.enrolment_mode !== 'new_and_historical' ||
      !String(version.version_code || '').trim()
    ) {
      setActionError('Could not publish syllabus. Its course or version is not eligible for publication.');
      setActionStatus('');
      return;
    }
    if (
      !effectiveFrom ||
      !isValidDateOnly(effectiveFrom) ||
      (effectiveTo && !isValidDateOnly(effectiveTo)) ||
      (effectiveTo && effectiveTo < effectiveFrom) ||
      (effectiveTo && effectiveTo < new Date().toISOString().slice(0, 10))
    ) {
      setActionError('Could not publish syllabus. Enter a valid effective start date and an optional end date that is not expired and is on or after the start date.');
      setActionStatus('');
      return;
    }
    if (
      exercises.length === 0 ||
      exercises.some(
        (exercise) =>
          !String(exercise.exercise_reference || '').trim() ||
          !String(exercise.title || '').trim()
      ) ||
      items.length === 0 ||
      items.some((item) => !String(item.description || '').trim())
    ) {
      setActionError('Could not publish syllabus. It must contain complete training exercises and at least one complete lesson assessment item.');
      setActionStatus('');
      return;
    }

    const effectiveEnd = effectiveTo || '9999-12-31';
    const overlapsPublishedVersion = data.syllabusVersions.some((published) => (
      published.id !== version.id &&
      String(published.course_id) === String(version.course_id) &&
      published.status === 'published' &&
      (published.effective_from || '0001-01-01') <= effectiveEnd &&
      effectiveFrom <= (published.effective_to || '9999-12-31')
    ));
    if (overlapsPublishedVersion) {
      setActionError('Could not publish syllabus. Another published version has an overlapping effective date range.');
      setActionStatus('');
      return;
    }

    if (!window.confirm(
      `Publish ${course.code} ${version.version_code} for ${effectiveFrom}${effectiveTo ? ` through ${effectiveTo}` : ' onward'}? Published syllabus content cannot be edited.`
    )) return;

    savingRef.current = true;
    setSaving(true);
    setSavingAction(`publish-${version.id}`);
    setActionError('');
    setActionStatus('');
    try {
      const result = await publishSyllabus(supabase, {
        syllabusVersionId: version.id,
        effectiveFrom,
        effectiveTo,
      });
      if (result.error) throw result.error;
      setActionStatus(`${course.code} ${version.version_code} was published. Published content is immutable.`);
      await reloadData();
    } catch (error) {
      setActionError(`Could not publish syllabus. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  async function handleUpdateInstructorAssignment(enrolmentId, currentInstructorProfileId) {
    const hasDraft = Object.prototype.hasOwnProperty.call(
      assignmentDrafts,
      String(enrolmentId)
    );
    const instructorProfileId = hasDraft
      ? assignmentDrafts[String(enrolmentId)]
      : currentInstructorProfileId;
    if (savingRef.current || !instructorProfileId) return;
    savingRef.current = true;
    setSaving(true);
    setSavingAction(`assignment-${enrolmentId}`);
    setActionError('');
    setActionStatus('');
    try {
      const result = await updateCourseEnrolmentInstructor(supabase, {
        organisationId,
        enrolmentId,
        instructorProfileId,
      });
      if (result.error) throw result.error;
      setAssignmentDrafts((current) => {
        const next = { ...current };
        delete next[String(enrolmentId)];
        return next;
      });
      setActionStatus('Primary instructor assignment updated.');
      await reloadData();
    } catch (error) {
      setActionError(`Could not update instructor assignment. ${error?.message || 'Please try again.'}`);
    } finally {
      savingRef.current = false;
      setSaving(false);
      setSavingAction('');
    }
  }

  const memberById = new Map(
    (data?.members || []).map((member) => [String(member.id), member])
  );
  const instructorById = new Map(
    (data?.instructors || []).map((instructor) => [String(instructor.id), instructor])
  );
  const courseById = new Map(
    (data?.courses || []).map((course) => [String(course.id), course])
  );
  const syllabusById = new Map(
    (data?.syllabusVersions || []).map((syllabus) => [String(syllabus.id), syllabus])
  );
  const selectedStudentOtherBookings = selectedStudent
    ? (data?.bookings || []).filter(
      (booking) =>
        String(booking.student_user_id || '') === String(selectedStudent.user_id) &&
        !booking.course_enrolment_id
    )
    : [];

  return (
    <main className="student-training-page">
      <header className="st-page-header">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="st-back-button"
        >
          Back to admin
        </button>
        <h1 className="st-page-title">Student training</h1>
        <p className="st-page-intro">
          Manage student enrolments and review syllabus-linked training activity.
        </p>
      </header>

      {loading && (
        <div className="st-loading" role="status" aria-live="polite">
          Loading student training data…
        </div>
      )}
      {actionError && (
        <div className="st-feedback st-feedback-error" role="alert">
          <span className="st-feedback-title">Action failed.</span>
          {actionError}
        </div>
      )}
      {actionStatus && (
        <div className="st-feedback st-feedback-success" role="status" aria-live="polite">
          <span className="st-feedback-title">Saved.</span>
          {actionStatus}
        </div>
      )}
      {loadError && (
        <section className="st-panel" role="alert">
          <h2 className="st-panel-heading">Training records unavailable</h2>
          {loadError}
          <p className="st-help-text">
            Check that the training tables, grants and organisation-scoped policies are available for this account.
          </p>
          <button type="button" onClick={reloadData} className="st-primary-button">
            Retry
          </button>
        </section>
      )}

      {!loading && !loadError && data && (
        <>
          <section className="st-panel" aria-labelledby="student-profile-heading">
            <h2 id="student-profile-heading" className="st-panel-heading">
              Student profiles
            </h2>
            <form onSubmit={handleCreateStudent}>
              <div className="st-form-grid">
                <label className="st-field">
                  Find an existing member
                <input
                  type="search"
                  aria-label="Find existing member"
                  value={memberSearch}
                  onChange={(event) => {
                    setMemberSearch(event.target.value);
                    setSelectedMemberId('');
                  }}
                  placeholder="Search name or email"
                    className="st-control"
                />
                </label>
                <label className="st-field">
                  Registered member without a profile
                  <select
                    aria-label="Select existing member"
                    value={selectedMemberId}
                    onChange={(event) => setSelectedMemberId(event.target.value)}
                    className="st-control"
                  >
                    <option value="">Select a member</option>
                    {membersWithoutProfiles.map((member) => (
                      <option key={member.id} value={member.id}>{memberLabel(member)}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="st-action-row">
                <button
                  type="submit"
                  disabled={saving || !selectedMemberId}
                  className="st-primary-button"
                  aria-busy={saving && savingAction === 'student'}
                >
                  {savingAction === 'student' ? 'Creating profile…' : 'Create student profile'}
                </button>
              </div>
              {membersWithoutProfiles.length === 0 && (
                <p className="st-help-text">{memberSearch.trim()
                  ? 'No matching members without a student profile were found.'
                  : 'No eligible registered members without a student profile were found.'}</p>
              )}
            </form>
          </section>

          <section className="st-panel" aria-labelledby="instructor-profile-heading">
            <h2 id="instructor-profile-heading" className="st-panel-heading">
              Instructor profiles
            </h2>
            <p className="st-help-text">
              Creating an active profile grants this organisation's member access to instructor training workflows. Database policies still enforce organisation boundaries and assigned-booking assessment permissions.
            </p>
            <form onSubmit={handleCreateInstructor}>
              <div className="st-form-grid">
                <label className="st-field">
                  Registered active member without an instructor profile
                  <select
                    aria-label="Select instructor member"
                    value={selectedInstructorMemberId}
                    onChange={(event) => setSelectedInstructorMemberId(event.target.value)}
                    className="st-control"
                  >
                    <option value="">Select a member</option>
                    {membersWithoutInstructorProfiles.map((member) => (
                      <option key={member.id} value={member.id}>{memberLabel(member)}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="st-action-row">
                <button
                  type="submit"
                  disabled={saving || !selectedInstructorMemberId}
                  className="st-primary-button"
                  aria-busy={saving && savingAction === 'instructor'}
                >
                  {savingAction === 'instructor' ? 'Creating profile…' : 'Create instructor profile'}
                </button>
              </div>
            </form>
            {data.instructors.length === 0 ? (
              <p className="st-empty-state">No instructor profiles exist for this organisation yet.</p>
            ) : (
              <ul className="st-booking-list" aria-label="Instructor profiles">
                {data.instructors.map((instructor) => {
                  const member = memberById.get(String(instructor.user_id));
                  const instructorName = member
                    ? memberLabel(member)
                    : 'Registered member';
                  const notesKey = String(instructor.id);
                  const savedNotes = data.instructorNotes?.[notesKey] || '';
                  const noteDraftExists = Object.prototype.hasOwnProperty.call(
                    instructorNotesDrafts,
                    notesKey
                  );
                  const noteValue = noteDraftExists
                    ? instructorNotesDrafts[notesKey]
                    : savedNotes;
                  return (
                    <li className="st-booking-item" key={instructor.id}>
                      <div>
                        {instructorName}
                        {' · '}
                        <span className={`st-status${instructor.status === 'active' ? '' : ' st-status-inactive'}`}>
                          {instructor.status}
                        </span>
                      </div>
                      <label className="st-field">
                        Private instructor notes (administrators only)
                        <textarea
                          aria-label={`Private notes for ${instructorName}`}
                          value={noteValue}
                          onChange={(event) => setInstructorNotesDrafts((current) => ({
                            ...current,
                            [notesKey]: event.target.value,
                          }))}
                          rows={2}
                          className="st-control"
                        />
                      </label>
                      <div className="st-action-row">
                        <button
                          type="button"
                          className="st-primary-button"
                          disabled={
                            saving ||
                            String(noteValue || '').trim() ===
                              String(savedNotes || '').trim()
                          }
                          aria-busy={saving && savingAction === `instructor-notes-${notesKey}`}
                          onClick={() => handleSaveInstructorNotes(instructor.id)}
                        >
                          {savingAction === `instructor-notes-${notesKey}`
                            ? 'Saving notes…'
                            : 'Save instructor notes'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {membersWithoutInstructorProfiles.length === 0 && (
              <p className="st-help-text">No eligible active members without an instructor profile were found.</p>
            )}
          </section>

          <section className="st-panel" aria-labelledby="enrolment-heading">
            <h2 id="enrolment-heading" className="st-panel-heading">
              Enrol a student
            </h2>
            <form onSubmit={handleCreateEnrolment}>
              <div className="st-form-grid">
                <label className="st-field">
                  Student
                  <select value={selectedStudentId} onChange={(event) => setSelectedStudentId(event.target.value)} className="st-control">
                    <option value="">Select a student</option>
                    {data.students.filter((student) => student.status === 'active').map((student) => {
                      const member = memberById.get(String(student.user_id));
                      return <option key={student.id} value={student.id}>{member ? memberLabel(member) : 'Registered member'}</option>;
                    })}
                  </select>
                </label>
                <label className="st-field">
                  Active course
                  <select value={selectedCourseId} onChange={(event) => setSelectedCourseId(event.target.value)} className="st-control">
                    <option value="">Select a course</option>
                    {activeCourses.map((course) => (
                      <option key={course.id} value={course.id}>{course.code} · {course.name}</option>
                    ))}
                  </select>
                </label>
                <label className="st-field">
                  Published syllabus
                  <select value={selectedSyllabusId} onChange={(event) => setSelectedSyllabusId(event.target.value)} className="st-control">
                    <option value="">Select a published syllabus</option>
                    {eligibleSyllabi.map((syllabus) => (
                      <option key={syllabus.id} value={syllabus.id}>{syllabus.version_code}</option>
                    ))}
                  </select>
                </label>
                <label className="st-field">
                  Active instructor
                  <select value={selectedInstructorId} onChange={(event) => setSelectedInstructorId(event.target.value)} className="st-control">
                    <option value="">Select an instructor</option>
                    {data.instructors.filter((instructor) => instructor.status === 'active').map((instructor) => {
                      const member = memberById.get(String(instructor.user_id));
                      return <option key={instructor.id} value={instructor.id}>{member ? memberLabel(member) : 'Active instructor'}</option>;
                    })}
                  </select>
                </label>
              </div>
              <div className="st-action-row">
                <button
                  type="submit"
                  disabled={
                    saving ||
                    !selectedStudent ||
                    selectedStudent.status !== 'active' ||
                    !selectedCourseId ||
                    !selectedSyllabusId ||
                    !selectedInstructorId ||
                    selectedStudentEnrolments.some(
                      (enrolment) =>
                        enrolment.status === 'active' &&
                        String(enrolment.course_id) === String(selectedCourseId)
                    )
                  }
                  className="st-primary-button"
                  aria-busy={saving && savingAction === 'enrolment'}
                >
                  {savingAction === 'enrolment' ? 'Enrolling…' : 'Enrol student'}
                </button>
              </div>
              {selectedCourseId && eligibleSyllabi.length === 0 && (
                <p className="st-help-text" role="status">
                  No currently effective published syllabus is available for this course.
                </p>
              )}
              {draftSyllabusVersions.length > 0 && (
                <div className="st-help-text" role="status">
                  <strong>Draft syllabuses must be published before they can be used for enrolment:</strong>
                  <ul className="st-booking-list">
                    {draftSyllabusVersions.map((version) => {
                      const course = data.courses.find(
                        (candidate) => String(candidate.id) === String(version.course_id)
                      );
                      const versionExercises = data.trainingExercises.filter(
                        (exercise) => String(exercise.syllabus_version_id) === String(version.id)
                      );
                      const versionExerciseIds = new Set(
                        versionExercises.map((exercise) => String(exercise.id))
                      );
                      const versionItems = data.trainingExerciseItems.filter(
                        (item) => versionExerciseIds.has(String(item.exercise_id))
                      );
                      const versionDates = publicationDates[String(version.id)] || {};
                      return (
                        <li className="st-booking-item" key={version.id}>
                          <div>{course ? `${course.code} · ` : ''}{version.version_code} (draft)</div>
                          {isActiveOrganisationAdmin && (
                            <>
                              <div className="st-form-grid">
                                <label className="st-field">
                                  Effective from
                                  <input
                                    type="date"
                                    aria-label={`Effective from ${version.version_code}`}
                                    className="st-control"
                                    value={versionDates.effectiveFrom || ''}
                                    onChange={(event) => setPublicationDates((current) => ({
                                      ...current,
                                      [String(version.id)]: {
                                        ...current[String(version.id)],
                                        effectiveFrom: event.target.value,
                                      },
                                    }))}
                                  />
                                </label>
                                <label className="st-field">
                                  Effective to (optional)
                                  <input
                                    type="date"
                                    aria-label={`Effective to ${version.version_code}`}
                                    className="st-control"
                                    value={versionDates.effectiveTo || ''}
                                    onChange={(event) => setPublicationDates((current) => ({
                                      ...current,
                                      [String(version.id)]: {
                                        ...current[String(version.id)],
                                        effectiveTo: event.target.value,
                                      },
                                    }))}
                                  />
                                </label>
                              </div>
                              <p>
                                Structure: {versionExercises.length} exercise(s), {versionItems.length} lesson item(s).
                              </p>
                              <div className="st-action-row">
                                <button
                                  type="button"
                                  className="st-primary-button"
                                  disabled={saving || !course || course.status !== 'active' || course.enrolment_mode !== 'new_and_historical'}
                                  aria-busy={saving && savingAction === `publish-${version.id}`}
                                  onClick={() => handlePublishSyllabus(version)}
                                >
                                  {savingAction === `publish-${version.id}` ? 'Publishing…' : 'Publish syllabus'}
                                </button>
                              </div>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {!activeInstructorProfiles.length && (
                <p className="st-help-text">No active instructors are available for assignment.</p>
              )}
            </form>
          </section>

          <section className="st-panel st-student-record-panel" aria-labelledby="student-record-heading">
            <h2 id="student-record-heading" className="st-panel-heading">
              Student record
            </h2>
            <label className="st-field st-student-picker">
              View student
              <select value={selectedStudentId} onChange={(event) => setSelectedStudentId(event.target.value)} className="st-control">
                <option value="">Select a student</option>
                {data.students.map((student) => {
                  const member = memberById.get(String(student.user_id));
                  return <option key={student.id} value={student.id}>{member ? memberLabel(member) : 'Registered member'} ({student.status})</option>;
                })}
              </select>
            </label>
            {selectedStudent && (
              <>
                <h3 className="st-student-name">
                  {selectedStudentMember ? memberLabel(selectedStudentMember) : 'Student'}
                </h3>
                {selectedStudentEnrolments.length === 0 && (
                  <p className="st-empty-state">No course enrolments recorded.</p>
                )}
                {selectedStudentEnrolments.map((enrolment) => {
                  const course = courseById.get(String(enrolment.course_id));
                  const syllabus = syllabusById.get(String(enrolment.syllabus_version_id));
                  const instructor = instructorById.get(String(enrolment.primary_instructor_profile_id));
                  const instructorMember = instructor
                    ? memberById.get(String(instructor.user_id))
                    : null;
                  const assignmentKey = String(enrolment.id);
                  const hasAssignmentDraft = Object.prototype.hasOwnProperty.call(
                    assignmentDrafts,
                    assignmentKey
                  );
                  const assignmentValue = hasAssignmentDraft
                    ? assignmentDrafts[assignmentKey]
                    : enrolment.primary_instructor_profile_id || '';
                  const assignmentIsActive = activeInstructorProfiles.some(
                    (candidate) => String(candidate.id) === String(assignmentValue)
                  );
                  const assignedInstructorIsActive = activeInstructorProfiles.some(
                    (candidate) => String(candidate.id) ===
                      String(enrolment.primary_instructor_profile_id || '')
                  );
                  const bookings = data.bookings.filter(
                    (booking) => String(booking.course_enrolment_id || '') === String(enrolment.id)
                  );
                  const summary = bookingAssessmentSummary(data.bookings, enrolment.id);
                  const syllabusExercises = (data.trainingExercises || [])
                    .filter(
                      (exercise) =>
                        String(exercise.syllabus_version_id) ===
                        String(enrolment.syllabus_version_id)
                    )
                    .sort((a, b) => a.display_order - b.display_order);
                  const enrolmentRecords = (data.trainingRecordEntries || [])
                    .filter(
                      (entry) =>
                        String(entry.course_enrolment_id) === String(enrolment.id)
                    );
                  const normalizedAssessmentKeys = new Set(
                    enrolmentRecords
                      .filter((entry) => (
                        entry.record_type === 'lesson_assessment' &&
                        entry.booking_id &&
                        entry.legacy_assessment_index
                      ))
                      .map((entry) => (
                        `${entry.booking_id}:${entry.legacy_assessment_index}`
                      ))
                  );
                  const legacyAssessmentRecords = bookings.flatMap((booking) => (
                    (Array.isArray(booking.briefing_data?.trainingAssessments)
                      ? booking.briefing_data.trainingAssessments
                      : []
                    ).map((assessment, index) => ({
                      id: `legacy-${booking.id}-${index + 1}`,
                      course_enrolment_id: enrolment.id,
                      record_type: 'lesson_assessment',
                      recorded_on: booking.date,
                      booking_id: booking.id,
                      outcome: assessment?.overallOutcome || '',
                      comments: assessment?.lessonNotes || '',
                      assessed_at: assessment?.assessedAt || null,
                      assessor_name: assessment?.assessedByName || '',
                      record_payload: assessment || {},
                      legacy_assessment_index: index + 1,
                    })).filter((entry) => (
                      !normalizedAssessmentKeys.has(
                        `${entry.booking_id}:${entry.legacy_assessment_index}`
                      )
                    ))
                  ));
                  const allEnrolmentRecords = [
                    ...enrolmentRecords,
                    ...legacyAssessmentRecords,
                  ];
                  const latestOutcomesByItem = latestTrainingItemOutcomes(allEnrolmentRecords);
                  const progressExercises = syllabusExercises.map((exercise) => ({
                    ...exercise,
                    items: (data.trainingExerciseItems || [])
                      .filter((item) => String(item.exercise_id) === String(exercise.id))
                      .sort((a, b) => a.display_order - b.display_order),
                  }));
                  const progressSummary = buildTrainingProgressSummary(
                    progressExercises,
                    allEnrolmentRecords
                  );
                  const recordSummary = buildTrainingRecordSummary(allEnrolmentRecords);
                  const orderedRecords = [...allEnrolmentRecords].sort((a, b) => (
                    (Date.parse(b.assessed_at || b.created_at || b.recorded_on || '') || 0) -
                    (Date.parse(a.assessed_at || a.created_at || a.recorded_on || '') || 0)
                  ));
                  const filteredExerciseProgress = progressExercises.map((exercise) => {
                    const matchingItems = exercise.items.filter((item) => {
                      const progress = latestOutcomesByItem.get(String(item.id));
                      const outcome = progress?.outcome || '';
                      const matchesStatus = exerciseStatusFilter === 'all' ||
                        (exerciseStatusFilter === 'not_assessed' ? !outcome : outcome === exerciseStatusFilter);
                      const query = exerciseSearch.trim().toLowerCase();
                      const matchesSearch = !query || [
                        exercise.exercise_reference,
                        exercise.title,
                        item.item_reference,
                        item.description,
                      ].some((value) => String(value || '').toLowerCase().includes(query));
                      return matchesStatus && matchesSearch;
                    });
                    const counts = exercise.items.reduce((result, item) => {
                      const outcome = latestOutcomesByItem.get(String(item.id))?.outcome;
                      if (outcome === 'satisfactory') result.satisfactory += 1;
                      else if (outcome === 'needs_practice') result.needsPractice += 1;
                      else result.notAssessed += 1;
                      return result;
                    }, { satisfactory: 0, needsPractice: 0, notAssessed: 0 });
                    const queryMatchesExercise = !exerciseSearch.trim() || [
                      exercise.exercise_reference,
                      exercise.title,
                    ].some((value) => String(value || '').toLowerCase().includes(exerciseSearch.trim().toLowerCase()));
                    return {
                      ...exercise,
                      matchingItems,
                      counts,
                      matches: queryMatchesExercise || matchingItems.length > 0,
                    };
                  }).filter((exercise) => exercise.matches);
                  const recordTabId = `student-record-${String(enrolment.id).replace(/[^a-z0-9_-]/gi, '-')}`;
                  const latestLesson = orderedRecords.find((entry) => entry.record_type === 'lesson_assessment');
                  const latestLessonPayload = latestLesson?.record_payload || {};
                  const assessedProgressPercent = progressSummary.itemCount
                    ? Math.round((progressSummary.satisfactoryCount / progressSummary.itemCount) * 100)
                    : 0;
                  return (
                    <article key={enrolment.id} className="st-enrolment-card">
                      <header className="st-record-header">
                        <h4 className="st-course-title">
                          {course
                            ? `${course.code} · ${course.name}${course.status === 'archived' ? ' (archived)' : ''}`
                            : 'Course'}
                        </h4>
                        <span className={`st-status${enrolment.status === 'active' ? '' : ' st-status-inactive'}`}>
                          {enrolment.status}
                        </span>
                      </header>
                      <dl className="st-record-details">
                        <div className="st-record-detail">
                          <dt>Syllabus version</dt>
                          <dd>
                            {syllabus
                              ? `${syllabus.version_code}${syllabus.status === 'retired' ? ' (retired)' : ''}`
                              : 'Published version unavailable'}
                          </dd>
                        </div>
                        <div className="st-record-detail">
                          <dt>Primary instructor</dt>
                          <dd>{instructorMember ? memberLabel(instructorMember) : 'Not assigned / unavailable'}</dd>
                        </div>
                        <div className="st-record-detail">
                          <dt>Started</dt>
                          <dd>{enrolment.start_date || 'Not recorded'}</dd>
                        </div>
                      </dl>
                      <div className="st-record-actions">
                        <button
                          type="button"
                          className="st-primary-button"
                          onClick={() => downloadTrainingRecordCsv({
                            studentName: selectedStudentMember?.full_name || 'Student',
                            courseName: course?.name || 'Training course',
                            versionCode: syllabus?.version_code || '',
                            entries: allEnrolmentRecords,
                            exercises: progressExercises,
                            items: data.trainingExerciseItems || [],
                          })}
                        >
                          Export training record CSV
                        </button>
                        <button
                          type="button"
                          className="st-secondary-button"
                          onClick={() => window.print()}
                        >
                          Print student record
                        </button>
                      </div>
                      {enrolment.status === 'active' && (
                        <div className="st-assignment-controls">
                          <label className="st-field">
                            Primary instructor assignment
                            <select
                              aria-label={`Primary instructor assignment ${enrolment.id}`}
                              value={assignmentValue}
                              onChange={(event) => setAssignmentDrafts((current) => ({
                                ...current,
                                [assignmentKey]: event.target.value,
                              }))}
                              className="st-control"
                            >
                              <option value="">Select an active instructor</option>
                              {activeInstructorProfiles.map((profile) => {
                                const member = memberById.get(String(profile.user_id));
                                return (
                                  <option key={profile.id} value={profile.id}>
                                    {member ? memberLabel(member) : 'Active instructor'}
                                  </option>
                                );
                              })}
                              {instructor && instructor.status !== 'active' && (
                                <option value={instructor.id}>
                                  {instructorMember ? memberLabel(instructorMember) : 'Instructor'} (inactive)
                                </option>
                              )}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="st-primary-button"
                            disabled={
                              saving ||
                              enrolment.status !== 'active' ||
                              !assignmentIsActive ||
                              (
                                String(assignmentValue) ===
                                String(enrolment.primary_instructor_profile_id || '') &&
                                assignedInstructorIsActive
                              )
                            }
                            aria-busy={saving && savingAction === `assignment-${enrolment.id}`}
                            onClick={() => handleUpdateInstructorAssignment(
                              enrolment.id,
                              enrolment.primary_instructor_profile_id
                            )}
                          >
                            {savingAction === `assignment-${enrolment.id}`
                              ? 'Saving assignment…'
                              : 'Update instructor assignment'}
                          </button>
                        </div>
                      )}

                      <div
                        className="st-tabs"
                        role="tablist"
                        aria-label={`${course?.name || 'Course'} student record sections`}
                        onKeyDown={(event) => {
                          const currentIndex = STUDENT_RECORD_SECTIONS.indexOf(activeRecordSection);
                          let nextIndex = currentIndex;
                          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % STUDENT_RECORD_SECTIONS.length;
                          else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + STUDENT_RECORD_SECTIONS.length) % STUDENT_RECORD_SECTIONS.length;
                          else if (event.key === 'Home') nextIndex = 0;
                          else if (event.key === 'End') nextIndex = STUDENT_RECORD_SECTIONS.length - 1;
                          else return;
                          event.preventDefault();
                          setActiveRecordSection(STUDENT_RECORD_SECTIONS[nextIndex]);
                          event.currentTarget.querySelectorAll('[role="tab"]')[nextIndex]?.focus();
                        }}
                      >
                        {STUDENT_RECORD_SECTIONS.map((section, index) => (
                          <button
                            key={section}
                            id={`${recordTabId}-tab-${index}`}
                            type="button"
                            role="tab"
                            aria-selected={activeRecordSection === section}
                            aria-controls={`${recordTabId}-panel`}
                            tabIndex={activeRecordSection === section ? 0 : -1}
                            className="st-tab"
                            onClick={() => setActiveRecordSection(section)}
                          >
                            {section}
                          </button>
                        ))}
                      </div>
                      <div
                        id={`${recordTabId}-panel`}
                        className="st-tab-panel"
                        role="tabpanel"
                        aria-labelledby={`${recordTabId}-tab-${STUDENT_RECORD_SECTIONS.indexOf(activeRecordSection)}`}
                        tabIndex={0}
                      >
                        {activeRecordSection === 'Overview' && (
                          <section aria-label="Student training overview">
                            <h5 className="st-section-title">Assessment progress</h5>
                            <div className="st-progress-heading">
                              <span>{progressSummary.satisfactoryCount} of {progressSummary.itemCount} criteria satisfactory</span>
                              <strong>{assessedProgressPercent}%</strong>
                            </div>
                            <div
                              className="st-progress-track"
                              role="progressbar"
                              aria-label="Satisfactory assessment criteria"
                              aria-valuemin="0"
                              aria-valuemax={progressSummary.itemCount}
                              aria-valuenow={progressSummary.satisfactoryCount}
                            >
                              <span style={{ width: `${assessedProgressPercent}%` }} />
                            </div>
                            <p className="st-record-note">
                              Assessment outcomes show recorded training progress only and are not formal syllabus completion records or licensing eligibility.
                            </p>
                            <div className="st-metrics" aria-label="Training assessment summary">
                              <div className="st-metric"><span className="st-metric-value">{summary.assessmentCount}</span><span className="st-metric-label">Lessons recorded</span></div>
                              <div className="st-metric"><span className="st-metric-value">{summary.satisfactoryCount}</span><span className="st-metric-label">Satisfactory</span></div>
                              <div className="st-metric"><span className="st-metric-value">{summary.needsPracticeCount}</span><span className="st-metric-label">Needs practice</span></div>
                              <div className="st-metric"><span className="st-metric-value">{summary.assessedItemCount}</span><span className="st-metric-label">Criteria assessed</span></div>
                            </div>
                            {latestLesson ? (
                              <div className="st-recent-lesson">
                                <h6 className="st-subheading">Latest lesson assessment</h6>
                                <p className="st-record-note">
                                  {latestLesson.recorded_on || 'Date not recorded'}
                                  {latestLesson.booking_id ? ` · Booking ${latestLesson.booking_id}` : ''}
                                  {latestLesson.assessor_name ? ` · ${latestLesson.assessor_name}` : ''}
                                </p>
                                <p className="st-record-note">
                                  {assessmentOutcomeLabel(latestLesson.outcome || latestLessonPayload.overallOutcome)}
                                  {(latestLesson.comments || latestLessonPayload.lessonNotes)
                                    ? ` · ${latestLesson.comments || latestLessonPayload.lessonNotes}`
                                    : ''}
                                </p>
                              </div>
                            ) : (
                              <p className="st-empty-state">No lesson assessments have been recorded yet.</p>
                            )}
                          </section>
                        )}

                        {activeRecordSection === 'Syllabus & Progress' && (
                          <section aria-label="Syllabus and exercise progress">
                            <div className="st-section-heading-row">
                              <div>
                                <h5 className="st-section-title">Syllabus exercises</h5>
                                <p className="st-help-text">
                                  {progressExercises.length} exercises · {progressSummary.itemCount} assessment criteria
                                </p>
                              </div>
                              <div className="st-syllabus-filters">
                                <label className="st-field">
                                  Search syllabus
                                  <input
                                    type="search"
                                    aria-label="Search syllabus exercises and criteria"
                                    value={exerciseSearch}
                                    onChange={(event) => setExerciseSearch(event.target.value)}
                                    placeholder="Exercise, number or criterion"
                                    className="st-control"
                                  />
                                </label>
                                <label className="st-field">
                                  Assessment status
                                  <select
                                    aria-label="Filter syllabus by assessment status"
                                    value={exerciseStatusFilter}
                                    onChange={(event) => setExerciseStatusFilter(event.target.value)}
                                    className="st-control"
                                  >
                                    <option value="all">All statuses</option>
                                    <option value="satisfactory">Satisfactory</option>
                                    <option value="needs_practice">Needs practice</option>
                                    <option value="not_assessed">Not assessed</option>
                                  </select>
                                </label>
                              </div>
                            </div>
                            <details className="st-syllabus-reference">
                              <summary>Syllabus source reference</summary>
                              <p>{syllabus?.source_reference || 'No separate source reference is recorded for this syllabus version.'}</p>
                              <p>Full original assessment criterion text remains available within each exercise.</p>
                            </details>
                            {progressExercises.length === 0 ? (
                              <p className="st-empty-state">No exercises are available for this assigned syllabus version.</p>
                            ) : filteredExerciseProgress.length === 0 ? (
                              <p className="st-empty-state" role="status">No exercises or criteria match these search and status filters.</p>
                            ) : (
                              <ol className="st-exercise-list">
                                {filteredExerciseProgress.map((exercise) => {
                                  const exerciseKey = `${enrolment.id}:${exercise.id}`;
                                  const hasExpansionChoice = Object.prototype.hasOwnProperty.call(expandedExercises, exerciseKey);
                                  const isExpanded = hasExpansionChoice
                                    ? expandedExercises[exerciseKey]
                                    : Boolean(exerciseSearch.trim() || exerciseStatusFilter !== 'all');
                                  const contentId = `${recordTabId}-exercise-${String(exercise.id).replace(/[^a-z0-9_-]/gi, '-')}`;
                                  const referenceDetails = getTrainingExerciseReferenceDetails(exercise.items);
                                  return (
                                    <li key={exercise.id} className="st-exercise-card">
                                      <button
                                        type="button"
                                        className="st-exercise-toggle"
                                        aria-expanded={isExpanded}
                                        aria-controls={contentId}
                                        onClick={() => setExpandedExercises((current) => ({
                                          ...current,
                                          [exerciseKey]: !isExpanded,
                                        }))}
                                      >
                                        <span className="st-exercise-number">{exercise.exercise_reference || '—'}</span>
                                        <span className="st-exercise-title">
                                          <strong>{exercise.title}</strong>
                                          <span>{exercise.items.length} criteria</span>
                                        </span>
                                        <span className="st-exercise-counts" aria-label={`${exercise.counts.satisfactory} satisfactory, ${exercise.counts.needsPractice} needs practice, ${exercise.counts.notAssessed} not assessed`}>
                                          <span className="st-count-satisfactory">{exercise.counts.satisfactory} satisfactory</span>
                                          <span className="st-count-practice">{exercise.counts.needsPractice} needs practice</span>
                                          <span className="st-count-unassessed">{exercise.counts.notAssessed} not assessed</span>
                                        </span>
                                        <span className="st-exercise-chevron" aria-hidden="true">{isExpanded ? '−' : '+'}</span>
                                      </button>
                                      {isExpanded && (
                                        <div id={contentId}>
                                          {referenceDetails.tags.length > 0 || referenceDetails.sourceMappings.length > 0 ? (
                                            <details className="st-exercise-reference">
                                              <summary>Exercise references and notes</summary>
                                              {referenceDetails.sourceMappings.map((reference) => (
                                                <p key={reference}><strong>CAA reference:</strong> {reference}</p>
                                              ))}
                                              {referenceDetails.tags.length > 0 ? (
                                                <p><strong>Criterion notes:</strong> {referenceDetails.tags.join(' · ')}</p>
                                              ) : null}
                                            </details>
                                          ) : null}
                                          <ol className="st-criteria-list">
                                            {exercise.matchingItems.length === 0 ? (
                                              <li className="st-empty-state">No criteria in this exercise match the selected filters.</li>
                                            ) : exercise.matchingItems.map((item) => {
                                              const progress = latestOutcomesByItem.get(String(item.id));
                                              const outcome = progress?.outcome || '';
                                              const criterion = presentTrainingCriterion(item.description);
                                              return (
                                                <li key={item.id} className="st-criterion-row">
                                                  <div className="st-criterion-copy">
                                                    <span className="st-criterion-reference">{item.item_reference || 'Criterion'}</span>
                                                    <span>{criterion.text}</span>
                                                  </div>
                                                  <span className={`st-outcome-badge st-outcome-${outcome || 'unassessed'}`}>
                                                    {assessmentOutcomeLabel(outcome)}
                                                  </span>
                                                </li>
                                              );
                                            })}
                                          </ol>
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </section>
                        )}

                        {activeRecordSection === 'Lessons & Assessments' && (
                          <section aria-label="Lessons and assessments">
                            <h5 className="st-section-title">Training bookings ({bookings.length})</h5>
                            {bookings.length === 0 ? (
                              <p className="st-empty-state">No training bookings recorded for this enrolment.</p>
                            ) : (
                              <ul className="st-booking-list">
                                {[...bookings].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))).map((booking) => (
                                  <li key={booking.id} className="st-booking-item">
                                    <span className="st-booking-date">{booking.date || 'Date not set'} {booking.time || ''}</span>
                                    <div>Flight time: {bookingFlightTime(booking) || 'Not recorded'}</div>
                                    <div>Booking reference: {booking.id}</div>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <h5 className="st-section-title">Saved instructor assessments</h5>
                            {orderedRecords.filter((entry) => entry.record_type === 'lesson_assessment').length === 0 ? (
                              <p className="st-empty-state">No lesson assessments or assessment corrections are recorded.</p>
                            ) : (
                              <ol className="st-history-list">
                                {orderedRecords.filter((entry) => entry.record_type === 'lesson_assessment').map((entry) => {
                                  const payload = entry.record_payload || {};
                                  return (
                                    <li key={entry.id} className="st-history-entry">
                                      <strong>{entry.recorded_on || 'Date not recorded'} · {assessmentOutcomeLabel(entry.outcome || payload.overallOutcome)}</strong>
                                      <div>Overall outcome: {assessmentOutcomeLabel(entry.outcome || payload.overallOutcome)}</div>
                                      {entry.booking_id ? <div>Booking {entry.booking_id}</div> : null}
                                      {(entry.assessor_name || payload.assessedByName) ? <div>Instructor sign-off: {entry.assessor_name || payload.assessedByName}</div> : null}
                                      {(entry.comments || payload.lessonNotes) ? <p>{entry.comments || payload.lessonNotes}</p> : null}
                                      {payload.nextLessonFocus ? <p>Next lesson focus: {payload.nextLessonFocus}</p> : null}
                                      <ul className="st-lesson-criteria">
                                        {(payload.exerciseAssessments || []).map((item, index) => (
                                          <li key={`${entry.id}-${item.itemId || index}`}>
                                            {item.itemReference || item.exerciseReference || 'Syllabus item'} · {assessmentOutcomeLabel(item.outcome)}
                                          </li>
                                        ))}
                                      </ul>
                                      {entry.correction_reason ? <p><strong>Correction reason:</strong> {entry.correction_reason}</p> : null}
                                      {entry.supersedes_entry_id ? <p>Supersedes entry {entry.supersedes_entry_id}</p> : null}
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                            {selectedStudentOtherBookings.length > 0 && (
                              <section className="st-other-bookings" aria-labelledby={`other-bookings-${recordTabId}`}>
                                <h5 id={`other-bookings-${recordTabId}`} className="st-section-title">Other training bookings</h5>
                                <ul className="st-booking-list">
                                  {selectedStudentOtherBookings.map((booking) => (
                                    <li key={booking.id} className="st-booking-item">
                                      <span className="st-booking-date">{booking.date || 'Date not set'} {booking.time || ''} · No course enrolment</span>
                                      <div>Flight time: {bookingFlightTime(booking) || 'Not recorded'}</div>
                                      {(booking.briefing_data?.trainingAssessments || []).map((assessment, index) => (
                                        <p className="st-record-note" key={`${booking.id}-${assessment.assessedAt || index}`}>
                                          {assessment.assessedByName ? `${assessment.assessedByName} · ` : ''}
                                          {assessmentOutcomeLabel(assessment.overallOutcome)}
                                          {assessment.lessonNotes ? ` · ${assessment.lessonNotes}` : ''}
                                        </p>
                                      ))}
                                    </li>
                                  ))}
                                </ul>
                              </section>
                            )}
                          </section>
                        )}

                        {activeRecordSection === 'Flight Hours' && (
                          <section aria-label="Training flight hours">
                            <h5 className="st-section-title">Recorded training time</h5>
                            <div className="st-metrics st-hour-metrics" aria-label="Digital training record summary">
                              <div className="st-metric"><span className="st-metric-value">{formatTrainingMinutes(recordSummary.dualMinutes)}</span><span className="st-metric-label">Dual flight</span></div>
                              <div className="st-metric"><span className="st-metric-value">{formatTrainingMinutes(recordSummary.soloMinutes)}</span><span className="st-metric-label">Solo flight</span></div>
                              <div className="st-metric"><span className="st-metric-value">{formatTrainingMinutes(recordSummary.simulatorMinutes)}</span><span className="st-metric-label">Simulator time</span></div>
                              <div className="st-metric"><span className="st-metric-value">{formatTrainingMinutes(recordSummary.creditMinutes)}</span><span className="st-metric-label">School-reviewed credits</span></div>
                            </div>
                            <p className="st-record-note">
                              Recorded training data only. School-reviewed credits and flight time do not certify syllabus completion or licensing eligibility.
                            </p>
                            <h5 className="st-section-title">Flight and credit entries</h5>
                            {orderedRecords.filter((entry) => ['flight', 'credit_review'].includes(entry.record_type)).length === 0 ? (
                              <p className="st-empty-state">No flight hours or reviewed credits have been recorded.</p>
                            ) : (
                              <ol className="st-history-list">
                                {orderedRecords.filter((entry) => ['flight', 'credit_review'].includes(entry.record_type)).map((entry) => (
                                  <li key={entry.id} className="st-history-entry">
                                    <strong>{entry.recorded_on || 'Date not recorded'} · {entry.record_type === 'flight' ? `${entry.flight_classification || 'Flight'} time` : 'School-reviewed credit'}</strong>
                                    <div>{entry.record_type === 'flight'
                                      ? `${formatTrainingMinutes(entry.flight_duration_minutes)}${entry.aircraft_registration ? ` · ${entry.aircraft_registration}` : ''}`
                                      : `${formatTrainingMinutes(entry.credit_minutes)} · ${entry.credit_basis || 'Review basis not recorded'}`}
                                    </div>
                                    {entry.assessor_name ? <div>Recorded by {entry.assessor_name}</div> : null}
                                    {entry.correction_reason ? <div>Correction: {entry.correction_reason}</div> : null}
                                    {entry.supersedes_entry_id ? <div>Supersedes entry {entry.supersedes_entry_id}</div> : null}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </section>
                        )}

                        {activeRecordSection === 'Training History' && (
                          <section aria-label="Append-only training history">
                            <h5 className="st-section-title">Digital training record · append-only history</h5>
                            <p className="st-help-text">
                              Entries are preserved with their attribution and timestamps. Corrections append a superseding entry; earlier records are never edited or removed.
                            </p>
                            {orderedRecords.length === 0 ? (
                              <p className="st-empty-state">No digital training records have been appended.</p>
                            ) : (
                              <ol className="st-history-list">
                                {orderedRecords.map((entry) => {
                                  const payload = entry.record_payload || {};
                                  const timestamp = entry.assessed_at || payload.assessedAt || entry.created_at;
                                  return (
                                    <li key={entry.id} className={`st-history-entry${entry.supersedes_entry_id ? ' st-history-correction' : ''}`}>
                                      <div className="st-history-entry-heading">
                                        <strong>{entry.recorded_on || 'Date not recorded'} · {trainingRecordTypeLabel(entry.record_type)}</strong>
                                        {entry.supersedes_entry_id ? <span className="st-history-tag">Correction</span> : null}
                                        {entry.record_type === 'credit_review' ? <span className="st-history-tag">School-reviewed credit</span> : null}
                                      </div>
                                      {entry.record_type === 'flight' && (
                                        <div>{entry.flight_classification === 'simulator' ? 'Simulator' : entry.flight_classification === 'solo' ? 'Solo flight' : 'Dual flight'} · {formatTrainingMinutes(entry.flight_duration_minutes)} · {entry.aircraft_registration || 'No aircraft registration'}</div>
                                      )}
                                      {entry.record_type === 'credit_review' ? <div>{formatTrainingMinutes(entry.credit_minutes)} · {entry.credit_basis || 'Review basis not recorded'}</div> : null}
                                      {entry.record_type === 'theory_exam' || entry.record_type === 'skill_test' ? <div>{entry.subject || trainingRecordTypeLabel(entry.record_type)} · {entry.outcome || 'No result recorded'}</div> : null}
                                      {entry.record_type === 'lesson_assessment' ? <div>Instructor outcome: {assessmentOutcomeLabel(entry.outcome || payload.overallOutcome)}</div> : null}
                                      {(entry.assessor_name || payload.assessedByName) ? <div>Instructor: {entry.assessor_name || payload.assessedByName}</div> : null}
                                      {timestamp ? <time dateTime={timestamp} className="st-history-timestamp">{new Date(timestamp).toLocaleString()}</time> : null}
                                      {(entry.comments || payload.lessonNotes) ? <p>{entry.comments || payload.lessonNotes}</p> : null}
                                      {entry.correction_reason ? <p><strong>Correction reason:</strong> {entry.correction_reason}</p> : null}
                                      {entry.supersedes_entry_id ? <p>Supersedes entry {entry.supersedes_entry_id}</p> : null}
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                            <p className="st-record-note">
                              Assessment outcomes are not formal syllabus completion records. Credits are school-reviewed entries; neither represents licensing eligibility.
                            </p>
                          </section>
                        )}
                      </div>
                      <section className="st-print-report" hidden aria-label="Printable student training record">
                        <h1>{selectedStudentMember?.full_name || 'Student'} · {course?.code || ''} {course?.name || 'Training course'}</h1>
                        <dl className="st-record-details">
                          <div className="st-record-detail"><dt>Syllabus version</dt><dd>{syllabus?.version_code || 'Unavailable'}</dd></div>
                          <div className="st-record-detail"><dt>Primary instructor</dt><dd>{instructorMember ? memberLabel(instructorMember) : 'Not assigned / unavailable'}</dd></div>
                          <div className="st-record-detail"><dt>Enrolment status</dt><dd>{enrolment.status}</dd></div>
                          <div className="st-record-detail"><dt>Started</dt><dd>{enrolment.start_date || 'Not recorded'}</dd></div>
                        </dl>
                        <h2>Training summary</h2>
                        <p>
                          Dual {formatTrainingMinutes(recordSummary.dualMinutes)} · Solo {formatTrainingMinutes(recordSummary.soloMinutes)} ·
                          {' '}Simulator {formatTrainingMinutes(recordSummary.simulatorMinutes)} · {summary.assessmentCount} saved lessons ·
                          {' '}{progressSummary.satisfactoryCount}/{progressSummary.itemCount} criteria satisfactory
                        </p>
                        <p>Assessment outcomes are not formal syllabus completion records or licensing eligibility.</p>
                        <h2>Syllabus &amp; progress</h2>
                        <p>{syllabus?.source_reference || 'No separate syllabus source reference is recorded.'}</p>
                        <ol className="st-print-criteria">
                          {progressExercises.map((exercise) => (
                            <li key={exercise.id}>
                              <strong>{exercise.exercise_reference} · {exercise.title}</strong>
                              <ol>
                                {exercise.items.map((item) => (
                                  <li key={item.id}>
                                    {item.item_reference ? `${item.item_reference} · ` : ''}
                                    {item.description} — {assessmentOutcomeLabel(latestOutcomesByItem.get(String(item.id))?.outcome)}
                                  </li>
                                ))}
                              </ol>
                            </li>
                          ))}
                        </ol>
                        <h2>Training bookings</h2>
                        <ul>
                          {[...bookings].sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))).map((booking) => (
                            <li key={booking.id}>
                              {booking.date || 'Date not set'} {booking.time || ''} · Booking {booking.id} · Flight time {bookingFlightTime(booking) || 'Not recorded'}
                            </li>
                          ))}
                        </ul>
                        <h2>Append-only training history</h2>
                        <ol>
                          {orderedRecords.map((entry) => {
                            const payload = entry.record_payload || {};
                            return (
                              <li key={entry.id}>
                                <strong>{entry.recorded_on || 'Date not recorded'} · {trainingRecordTypeLabel(entry.record_type)}</strong>
                                {entry.record_type === 'flight' ? <div>{entry.flight_classification || 'Flight'} · {formatTrainingMinutes(entry.flight_duration_minutes)} · {entry.aircraft_registration || 'No aircraft registration'}</div> : null}
                                {entry.record_type === 'credit_review' ? <div>School-reviewed credit · {formatTrainingMinutes(entry.credit_minutes)} · {entry.credit_basis || 'Review basis not recorded'}</div> : null}
                                {entry.record_type === 'lesson_assessment' ? <div>Instructor assessment · {assessmentOutcomeLabel(entry.outcome || payload.overallOutcome)}</div> : null}
                                {(entry.assessor_name || payload.assessedByName) ? <div>Instructor: {entry.assessor_name || payload.assessedByName}</div> : null}
                                {entry.comments || payload.lessonNotes ? <div>{entry.comments || payload.lessonNotes}</div> : null}
                                {entry.correction_reason ? <div>Correction reason: {entry.correction_reason}</div> : null}
                                {entry.supersedes_entry_id ? <div>Supersedes entry {entry.supersedes_entry_id}</div> : null}
                                {entry.assessed_at || entry.created_at ? <time dateTime={entry.assessed_at || entry.created_at}>{new Date(entry.assessed_at || entry.created_at).toLocaleString()}</time> : null}
                              </li>
                            );
                          })}
                        </ol>
                      </section>
                    </article>
                  );
                })}
              </>
            )}
          </section>
        </>
      )}
    </main>
  );
}

export { bookingAssessmentSummary };
